import React, { ReactNode } from 'react';
import { 
  Clock, Pill, Info, AlertCircle, Zap, Brain, Smile, Moon, Shield, 
  Dumbbell, Activity, FlaskConical, Stethoscope, Bone, Eye, 
  BatteryCharging, HeartPulse, Flame, Microscope, Tablets, CircleHelp, PenLine, ExternalLink,
  HandCoins
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { RecommendationProps } from './RecommendationCard';
import { cn } from '@/lib/utils';
import { useUserData } from '@/context/UserDataContext';
import { getBenefitIcon } from '@/constants/ui';

interface SupplementDetailsProps {
  supplement: RecommendationProps;
}

interface InfoBlockProps {
  icon: ReactNode;
  title: string;
  iconColor?: string;
  children: ReactNode;
  className?: string;
  containerClassName?: string;
  size?: 'full' | 'half';
}

const InfoBlock: React.FC<InfoBlockProps> = ({ 
  icon, 
  title, 
  iconColor = "text-blue-500", 
  children,
  className,
  containerClassName,
  size = 'full'
}) => {
  return (
    <div className={cn(
      "my-6",
      size === 'half' && 'lg:w-1/2',
      containerClassName
    )}>
      <div className={cn("flex items-center gap-2 mb-2", className)}>
        {React.cloneElement(icon as React.ReactElement, { className: `h-5 w-5 ${iconColor}` })}
        <h3 className="font-bold font-['Merriweather']">{title}</h3>
      </div>
      <div className="text-sm text-muted-foreground ml-7 whitespace-pre-line">
        {children}
      </div>
    </div>
  );
};


const whereToBuyClinical = `In order to remain unbiased, we won't recommend specific brands.

Given this is a clinical supplement (i.e. well established and extensively researched) it will most likelybe found at your local chemist.
Otherwise, we'd recommend an online retailer such as iHerb. They have a great reputation and are a trusted retailer for all of the top brands.`


const whereToBuyHerbal = `In order to remain unbiased, we won't recommend specific brands.

Given this is a herbal supplement (i.e. plant based, generally used in 'traditional medicine') it's unlikely to be found in any local stores.
Instead, we'd recommend an online retailer such as iHerb. They have a great reputation and are a trusted retailer for all of the top brands.`


const whereToBuyAlternative = ``


const SupplementDetails: React.FC<SupplementDetailsProps> = ({ supplement }) => {
  const { userData } = useUserData();
  
  // Get the detailed description for a benefit
  const getBenefitDescription = (benefit: string): string => {
    // Use the detailed benefit description if available
    return supplement.benefitsLong?.[benefit] || `${benefit} may provide health benefits based on clinical studies.`;
  };

  // Get the appropriate dosage based on user's gender
  const getDosage = () => {
    if (userData.gender === 'male' && supplement.dosage_male) {
      return supplement.dosage_male;
    } 
    else if (userData.gender === 'female' && supplement.dosage_female) {
      return supplement.dosage_female;
    } 
    // Fallback to general dosage if gender-specific one is not available
    return supplement.dosage || 'Dosage information not available';
  };

  return (
    <div className="pt-6 pb-6 lg:pb-20">
      {/* Hero section - sticky at the top */}
      <div className="sticky top-0 lg:top-4 py-6 px-6 rounded-xl z-10 backdrop-blur-lg lg:backdrop-blur-sm">
        <h1 className="text-[2rem] lg:text-[2.5rem] font-bold font-['Merriweather'] flex flex-col lg:flex-row lg:items-center lg:gap-3">
          {supplement.name}
          {supplement.priority === 'high' && (
            <Badge 
              variant="outline" 
              className="flex items-center gap-1.5 border-green-300 text-green-700 font-normal tracking-wide text-[0.7rem] lg:text-[0.8rem] w-fit mt-2 lg:mt-0"
            >
              <Flame className="h-3 w-3 text-green-600" />
              Top Recommendation
            </Badge>
          )}
        </h1>

        {/* Description text in sticky header. I like the look of it, but at the same time I think the issue is it takes up too much space.
        
        <div className="text-sm text-muted-foreground [&>p:not(:last-child)]:mb-4 max-w-[70%]" dangerouslySetInnerHTML={{ __html: supplement.full_description }} /> */}
      </div>
      <Separator className="mb-8" />
      {/* Content sections */}
      <div className="px-6">

        {/* Grid Layout */}
        <h1 className="text-3xl font-bold flex items-center gap-3 font-['Merriweather'] mb-8"> Key Information </h1>
        <div className="flex flex-col lg:flex-row lg:flex-wrap bg-white/80 backdrop-blur-sm rounded-lg border shadow-[0px_0px_20px_rgba(0,0,0,0.05)] px-4">
          {/* Left side - tall box */}
          <InfoBlock 
            icon={<CircleHelp />} 
            title={"What is " + supplement.name + "?"} 
            size="half"
            containerClassName="lg:pr-10"
          >
            <div className="[&>p:not(:last-child)]:mb-4" dangerouslySetInnerHTML={{ __html: supplement.full_description }} />
          </InfoBlock>

          {/* Right side - container for stacked boxes */}
          <div className="lg:w-1/2 flex flex-col">
            <InfoBlock 
              icon={<Microscope />} 
              title={"We're recommending it for you because..."} 
              size="full"
              containerClassName="mb-0 lg:pr-6"
            >
              <p>{supplement.reason}</p>
            </InfoBlock>

            <InfoBlock 
              icon={<Pill />} 
              title="Your Dosage" 
              size="full"
              containerClassName="mb-0 lg:pr-6"
            >
              <p>{getDosage()}</p>
            </InfoBlock>

            
        <InfoBlock icon={<PenLine />} title="Physician's Notes" containerClassName="lg:pr-6">
          <p>{supplement.notes}</p>
        </InfoBlock>
          </div>
        </div>

        <Separator className="my-12" />
        
        <h1 className="text-3xl font-bold flex items-center gap-3 font-['Merriweather'] mb-8 mt-12"> The Benefits </h1>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
            {supplement.benefits.map((benefit, index) => {
              const iconInfo = getBenefitIcon(benefit);
              return (
                <div 
                  key={index}
                  className="bg-white/80 backdrop-blur-sm rounded-lg p-4 border shadow-[0px_0px_20px_rgba(0,0,0,0.05)]"
                >
                  <div className="flex items-center gap-2 mb-2">
                    {React.cloneElement(iconInfo.icon as React.ReactElement, {
                      className: `h-5 w-5 ${iconInfo.className}`
                    })}
                    <h3 className="font-medium">{benefit}</h3>
                  </div>
                  <div 
                    className="text-sm text-muted-foreground [&>p:not(:last-child)]:mb-2" 
                    dangerouslySetInnerHTML={{ __html: getBenefitDescription(benefit) }} 
                  />
                </div>
              );
            })}
          </div>

          <Separator className="my-12" />

          <h1 className="text-3xl font-bold flex items-center gap-3 font-['Merriweather'] mb-8"> Good to Know </h1>
          <div className="flex flex-col lg:flex-row lg:flex-wrap bg-white/80 backdrop-blur-sm rounded-lg border shadow-[0px_0px_20px_rgba(0,0,0,0.05)] px-4 mb-10">
          {/* Left side - tall box */}
          <InfoBlock icon={<Tablets />} title="How to take it" size="half" containerClassName="lg:pr-6">
          <div className="[&>p:not(:last-child)]:mb-4" dangerouslySetInnerHTML={{ __html: supplement.how_to_take }} />
        </InfoBlock>

        
        <InfoBlock icon={<AlertCircle />} title="Precautions" iconColor="text-amber-500" size="half" containerClassName="lg:pr-6">
          <div className="[&>p:not(:last-child)]:mb-4" dangerouslySetInnerHTML={{ __html: supplement.precautions }} />
        </InfoBlock>

        </div>
        <Separator className="my-12" />
        <h1 className="text-3xl font-bold flex items-center gap-3 font-['Merriweather'] mb-8"> Where to buy </h1>
        {supplement.type.toLowerCase() === 'clinical' ? (
          <div className="flex flex-col lg:flex-row lg:flex-wrap bg-white/80 backdrop-blur-sm rounded-lg border shadow-[0px_0px_20px_rgba(0,0,0,0.05)] px-4 mb-4 lg:mb-10">
            <InfoBlock icon={<HandCoins />} title="Where you can buy it" iconColor="text-primary" containerClassName="pr-6">
              {whereToBuyClinical}          
            </InfoBlock>
            <div className="w-full ml-7 mt-2 mb-6">
              <Button 
                variant="default" 
                size="sm"
                className='p-2 px-4' 
                onClick={() => window.open(`https://www.iherb.com/search?kw=${encodeURIComponent(supplement.name)}`, '_blank')}
              >
                Search on iHerb
                <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        ) : supplement.type.toLowerCase() === 'herbal' ? (
          <div className="flex flex-col lg:flex-row lg:flex-wrap bg-white/80 backdrop-blur-sm rounded-lg border shadow-[0px_0px_20px_rgba(0,0,0,0.05)] px-4 mb-4 lg:mb-10">
            <InfoBlock icon={<HandCoins />} title="Where you can buy it" iconColor="text-primary" containerClassName="pr-6">
              {whereToBuyHerbal}          
            </InfoBlock>
            <div className="w-full ml-7 mt-2 mb-6">
              <Button 
                variant="default" 
                size="sm"
                className='p-2 px-4' 
                onClick={() => window.open(`https://www.iherb.com/search?kw=${encodeURIComponent(supplement.name)}`, '_blank')}
              >
                Search on iHerb
                <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row lg:flex-wrap bg-white/80 backdrop-blur-sm rounded-lg border shadow-[0px_0px_20px_rgba(0,0,0,0.05)] px-4 mb-4 lg:mb-10">
            <InfoBlock icon={<AlertCircle />} title="This is likely a banned substance" iconColor="text-amber-500" containerClassName="pr-6">
              This is {supplement.type.toLowerCase().startsWith('a') || supplement.type.toLowerCase().startsWith('e') || supplement.type.toLowerCase().startsWith('i') || supplement.type.toLowerCase().startsWith('o') || supplement.type.toLowerCase().startsWith('u') ? 'an' : 'a'} {supplement.type.toLowerCase()} that, given its approval status, is likely to be illegal in your jurisdiction or at the very least unavailable for direct purchase.
              <br />
              <br />
             
             It may be available through prescription. If you're interested in trying it, we'd recommend speaking to your doctor or specialist.

            </InfoBlock>


          </div>
        )}
      </div>
    </div>
  );
};

export default SupplementDetails; 